import { Immunity } from "../data/spawnRules";
import { Heading, TroopsData } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";
import { Cell } from "../world/cell";
import { CellKind, Grid } from "../world/grid";
import { Occupant, RoadMap } from "../world/roadMap";
import { SimConstants } from "./constants";
import { Enemy, Troop } from "./entities";
import { EventBuffer, SimEventKind } from "./events";

export * from "./troopRules";
import { TroopBark, formationSlot, troopBlow, troopTierMult, withinBark } from "./troopRules";
import { formationFacing, formationPointAt, nearestToFlag, troopRoute } from "./troopGeometry";

/**
 * The base's troops: spawn on a timer during waves, walk to the rally flag over non-blocked cells (BFS, deterministic
 * tie-break), then settle on their own slot of a formation across the road there (formationSlot), strike the nearest
 * ground enemy within reach, and stop ground enemies that walk into them. A troop's slot is its index in `slots`, which
 * never changes, so the formation is stable: a fallen soldier's place is the next recruit's.
 */
export class TroopSystem {
  readonly slots: Troop[] = [];
  /** Barracks soldiers (TOWERS_V2; sim/barracks.ts keeps them): troop entities with `ownerTowerId` set, fighting at their posts. */
  readonly soldiers: Troop[] = [];
  /** The flag the player planted (SetRally); null until then, when the troops hold at `defaultRally`. */
  private planted: Cell | null = null;
  /** The rally the living troops' routes lead to (tick re-routes them when the default rally moves). */
  private routedTo: Cell;
  spawnCountdown = 0;
  private nextId = 1;
  private readonly speedPerTick: Fix64;
  /** The fortress's in-run tier bonus on health and damage (troopTierMult); One until the fortress is upgraded. */
  private tierMult = Fix64.One;
  /** @internal the last `reserved` slots the spawn timer leaves empty (the Rally Horn's squad between its waves, sim/rallyHorn.ts). */
  reserved = 0;
  /** @internal Rally Bark while it lasts (sim/petBark.ts sets it each tick; null otherwise): fortress troops near the flag. */
  bark: TroopBark | null = null;
  /** @internal Shell Wall (Pets v3, sim/petAura.ts): fortress troops near the flag take less (damageMult unused). */
  guard: TroopBark | null = null;

  constructor(
    private readonly rules: TroopsData,
    private readonly grid: Grid,
    private readonly road: RoadMap,
    private readonly events: EventBuffer,
    private readonly statMult: Fix64,
    readonly spawnInterval: number,
    capBonus = 0,
  ) {
    for (let i = 0; i < rules.cap + capBonus; i++) this.slots.push(new Troop());
    this.spawnCountdown = spawnInterval;
    this.routedTo = this.rally;
    this.speedPerTick = rules.speedCellsPerSecond.divInt(SimConstants.TicksPerSecond);
  }

  /** @internal `n` more slots, kept empty (`reserved`) until opened (sim/rallyHorn.ts). */
  reserve(n: number): void { for (let i = 0; i < n; i++) this.slots.push(new Troop()); this.reserved += n; }
  /** @internal one troop now, outside the timer, into the first free slot the cap allows (sim/rallyHorn.ts). */
  muster(): void { this.spawn(); }

  /** Living fortress troops (Barracks soldiers not counted). */
  get aliveCount(): number { let n = 0; for (const t of this.slots) if (t.alive) n++; return n; }
  /** A living troop or Barracks soldier by id (ids are shared, so one never names both). */
  find(id: number): Troop | null {
    for (const t of this.slots) if (t.alive && t.id === id) return t;
    for (const t of this.soldiers) if (t.alive && t.id === id) return t;
    return null;
  }
  /** Every troop entity, fortress troops first, then Barracks soldiers (views draw both; `ownerTowerId` tells them apart). */
  get all(): readonly Troop[] { return this.soldiers.length === 0 ? this.slots : [...this.slots, ...this.soldiers]; }
  /** A fresh troop id from the counter the fortress troops use, so a Barracks soldier never shares one. */
  allocateId(): number { return this.nextId++; }
  /** Troop speed per tick (Barracks soldiers walk at it too). */
  get speed(): Fix64 { return this.speedPerTick; }

  /** A cell troops may stand on: in bounds, not rock, not an unbridged chasm, not a tower, not inside the fortress walls. Road and the gate are fine. */
  walkable(c: Cell): boolean {
    if (!this.grid.inBounds(c)) return false;
    const kind = this.grid.kindAt(c);
    if (kind === CellKind.Blocked) return false;
    // a chasm is a hole; a troop may stand there only where the road bridges it
    if (kind === CellKind.Chasm && !this.road.isRoad(c)) return false;
    if (kind === CellKind.Base && !c.equals(this.grid.base)) return false;
    return this.road.occupantAt(c) !== Occupant.Tower;
  }

  /** Where the troops gather: the planted flag, or before the player plants one, `defaultRally`. */
  get rally(): Cell { return this.planted ?? this.defaultRally; }
  /** Whether the player has planted the flag this run (views and the HUD may tell the default apart). */
  get flagPlanted(): boolean { return this.planted !== null; }

  /**
   * The rally point with no flag planted (DESIGN §7, Kevin 2026-10-08: "right outside the base"): the first road cell
   * out of the fortress gate, so the formation stands across the road in front of the walls; the base itself while
   * there is no road (or that cell cannot be stood on).
   */
  get defaultRally(): Cell {
    const cells = this.road.designedCells;
    return cells.length > 0 && this.walkable(cells[0]) ? cells[0] : this.grid.base;
  }

  /** Moves the flag; every troop re-routes. Returns false for an unwalkable cell. */
  setRally(c: Cell): boolean {
    if (!this.walkable(c)) return false;
    this.planted = c;
    this.routedTo = c;
    for (const t of this.slots) if (t.alive) t.path = this.pathFrom(new Cell(t.cx, t.cy));
    this.events.add(SimEventKind.RallyMoved, c.x, c.y);
    return true;
  }

  /** The in-run tier multiplier (troopTierMult) on the troops' health and damage. */
  get tierMultiplier(): Fix64 { return this.tierMult; }
  /** Everything on a troop's health and damage now: base level, VIP / gear troop bonus and the fortress's tier. */
  get strength(): Fix64 { return this.statMult.mul(this.tierMult); }

  /**
   * The fortress reached `tier` in this run: every troop, alive or yet to come, gets troopTierMult. A living troop keeps
   * its share of health (a wounded one is healed in proportion, never to full).
   */
  setBaseTier(tier: number): void {
    const next = troopTierMult(this.rules, tier);
    if (next.eq(this.tierMult)) return;
    const maxHp = this.rules.hp.mul(this.statMult).mul(next), damage = this.rules.damage.mul(this.statMult).mul(next);
    for (const t of this.slots) {
      if (!t.alive) continue;
      t.hp = t.hp.mul(next).div(this.tierMult);
      t.maxHp = maxHp;
      t.damage = damage;
    }
    this.tierMult = next;
  }

  /**
   * The way the formation faces: toward where enemies come from at the flag. On the road that is the next road cell
   * toward the portal (at the portal itself, away from the base side); off the road, the main axis toward the portal;
   * with no road yet, the way the road will leave the fortress.
   */
  get facing(): Heading { return this.facingAt(this.rally); }

  /** The way a formation at cell `r` faces (see `facing`); Barracks posts use it too. */
  facingAt(r: Cell): Heading { return formationFacing(this.road, this.grid, r); }

  /** Where slot `slot` stands now: the flag's cell centre plus its formation offset, facing `facing`. */
  slotPoint(slot: number, facing: Heading = this.facing): { readonly x: Fix64; readonly y: Fix64 } {
    return this.formationPoint(this.rally, slot, this.slots.length, this.rules.formationRows, facing);
  }

  /** Slot `slot` of a `count`-strong, `rows`-deep formation at cell `at`, facing `facing` (formationSlot). */
  formationPoint(at: Cell, slot: number, count: number, rows: number, facing: Heading): { readonly x: Fix64; readonly y: Fix64 } {
    return formationPointAt(this.rules, at, formationSlot(slot, count, rows), facing);
  }

  /** Towers placed or sold change what is walkable: recompute every route. */
  reroute(): void { for (const t of this.slots) if (t.alive) t.path = this.pathFrom(new Cell(t.cx, t.cy)); }

  /** Once per wave tick: spawn timer, walking, fighting. Enemies must already have moved this tick. */
  tick(enemies: readonly Enemy[], onHit: (e: Enemy, damage: Fix64) => void): void {
    // The default rally follows the road (its first cell out of the gate): should it move under the troops, they re-route.
    const rally = this.rally;
    if (!rally.equals(this.routedTo)) { this.routedTo = rally; this.reroute(); }
    this.spawnCountdown--;
    if (this.spawnCountdown <= 0) { this.spawnCountdown = this.spawnInterval; this.spawn(); }
    const facing = this.facing;
    for (let i = 0; i < this.slots.length; i++) {
      const t = this.slots[i];
      if (!t.alive) continue;
      if (t.path.length > 0) this.walk(t);
      else this.settle(t, this.slotPoint(i, facing));
      if (t.attackCooldown > 0) { t.attackCooldown--; continue; }
      const target = this.nearestGroundEnemy(t, enemies);
      if (target === null) continue;
      t.attackCooldown = this.rules.attackIntervalTicks;
      this.events.add(SimEventKind.TroopAttacked, t.id, target.id);
      onHit(target, this.barked(t) ? t.damage.mul(this.bark!.damageMult) : t.damage);
    }
  }

  /**
   * Called from enemy movement for each ground enemy before it steps: a living troop within block range holds it.
   * The blocked enemy strikes that troop on its own cadence (which runs on while it walks, so its first contact
   * strikes at once and a later one when the cadence comes round). Returns true when the enemy must not move this tick.
   */
  blocks(e: Enemy): boolean {
    if (e.isAir || e.burrowed) return false;            // a Burrower underground passes under the line (ENEMIES_V2)
    if ((e.immunity & Immunity.Physical) !== 0) return false;   // M36: troops cannot fight a raider immune to physical, so it walks through them
    let holder: Troop | null = null;
    for (const t of this.slots) {
      if (!t.alive) continue;
      if (e.x.sub(t.x).abs().le(this.rules.blockRange) && e.y.sub(t.y).abs().le(this.rules.blockRange)) { holder = t; break; }
    }
    // then the Barracks soldiers (TOWERS_V2), in their keeper's order
    if (holder === null) {
      for (const t of this.soldiers) {
        if (!t.alive) continue;
        const reach = t.blockBonus.isZero() ? this.rules.blockRange : this.rules.blockRange.add(t.blockBonus);   // Shield Wall (tier 4)
        if (e.x.sub(t.x).abs().le(reach) && e.y.sub(t.y).abs().le(reach)) { holder = t; break; }
      }
    }
    // Not held: the enemy's strike cadence keeps running down rather than resetting. Stepping from one soldier of the
    // formation to the next (the row behind, after the front one fell) must not earn it a free strike on contact: the
    // stacked troops never let it step, and a reset made the formation lose to the same raiders.
    if (holder === null) { if (e.attackCooldown > 0) e.attackCooldown--; return false; }
    if (e.attackCooldown === 0) this.events.add(SimEventKind.EnemyBlocked, e.id, holder.id);
    if (e.attackCooldown > 0) { e.attackCooldown--; return true; }
    e.attackCooldown = this.rules.enemyAttackIntervalTicks;
    const hit = e.maxHp.mul(e.hitFraction ?? this.rules.enemyHitFraction);   // the wild pet bites with its own share
    // Pets: a dazzled raider strikes for nothing; Rally Bark, Shell Wall and a Crystal Ward shield cut the blow (troopRules.ts)
    const r = this.rally, blow = troopBlow(holder, e.dazzleTicks > 0 ? Fix64.Zero : hit, this.bark, this.guard, r.x, r.y);
    holder.hp = holder.hp.sub(holder.armour.isZero() ? blow : blow.mul(Fix64.One.sub(holder.armour)));
    if (holder.hp.le(Fix64.Zero)) {
      holder.hp = Fix64.Zero; holder.alive = false; this.events.add(SimEventKind.TroopDied, holder.id, e.id);
      if (holder.ownerTowerId !== 0) holder.respawnLeft = this.onSoldierDied(holder);
    }
    return true;
  }

  /** Whether Rally Bark is on and `t` (a fortress troop) stands within its reach of the flag. */
  private barked(t: Troop): boolean {
    if (this.bark === null) return false;
    const r = this.rally;
    return withinBark(this.bark, r.x, r.y, t.x, t.y);
  }

  writeHash(h: Fnv1a64): void {
    const rally = this.rally;
    h.writeInt(rally.x); h.writeInt(rally.y); h.writeInt(this.planted === null ? 0 : 1); h.writeInt(this.spawnCountdown); h.writeInt(this.nextId); h.writeRaw(this.tierMult.raw);
    for (const t of this.slots) {
      if (!t.alive) continue;
      h.writeInt(t.id); h.writeRaw(t.hp.raw); h.writeRaw(t.x.raw); h.writeRaw(t.y.raw); h.writeInt(t.path.length); h.writeInt(t.attackCooldown);
    }
  }

  private spawn(): void {
    let t: Troop | undefined;
    for (let i = 0, n = this.slots.length - this.reserved; i < n && t === undefined; i++) if (!this.slots[i].alive) t = this.slots[i];
    if (!t) return; // at cap
    t.id = this.nextId++;
    t.alive = true;
    t.maxHp = this.rules.hp.mul(this.statMult).mul(this.tierMult);
    t.hp = t.maxHp;
    t.damage = this.rules.damage.mul(this.statMult).mul(this.tierMult);
    t.cx = this.grid.base.x; t.cy = this.grid.base.y;
    t.x = Fix64.fromInt(t.cx); t.y = Fix64.fromInt(t.cy);
    t.attackCooldown = 0;
    t.shield = Fix64.Zero; t.shieldTicks = 0;
    t.path = this.pathFrom(this.grid.base);
    this.events.add(SimEventKind.TroopSpawned, t.id, t.cx, t.cy);
  }

  /** Ticks until a fallen Barracks soldier musters again (set by sim/barracks.ts). */
  onSoldierDied: (t: Troop) => number = () => 0;

  /**
   * Steps along the path; cells are grid-adjacent so every move is axis-aligned (no square roots). One axis at a time,
   * each clamped onto the next cell's centre line: a troop leaving its formation slot (off the cell centre) must reach
   * the line exactly. (It used to snap only when both axes were within a step, so a soldier standing on a slot that is
   * not a whole number of steps from the centre jittered across the line for ever and never left: a flag planted after
   * the troops had formed up got no troops, and no new ones came while the stuck ones filled the cap — M30 #20.)
   */
  walk(t: Troop): void {
    const next = t.path[0];
    const tx = Fix64.fromInt(next.x), ty = Fix64.fromInt(next.y);
    const dx = tx.sub(t.x), dy = ty.sub(t.y);
    const step = this.speedPerTick;
    if (dx.abs().le(step) && dy.abs().le(step)) {
      t.x = tx; t.y = ty; t.cx = next.x; t.cy = next.y;
      t.path.shift();
      return;
    }
    if (!dx.isZero()) t.x = dx.abs().le(step) ? tx : dx.isPositive() ? t.x.add(step) : t.x.sub(step);
    else t.y = dy.isPositive() ? t.y.add(step) : t.y.sub(step);
  }

  /**
   * At the flag's cell: steps toward the troop's formation slot, one axis at a time like `walk` (no square roots). The
   * slot lies inside the flag's cell or at its edge, so the troop never crosses into a cell it could not walk.
   */
  settle(t: Troop, to: { readonly x: Fix64; readonly y: Fix64 }): void {
    const dx = to.x.sub(t.x), dy = to.y.sub(t.y), step = this.speedPerTick;
    if (!dx.isZero()) t.x = dx.abs().le(step) ? to.x : dx.isPositive() ? t.x.add(step) : t.x.sub(step);
    else if (!dy.isZero()) t.y = dy.abs().le(step) ? to.y : dy.isPositive() ? t.y.add(step) : t.y.sub(step);
  }

  /**
   * The ground enemy this troop strikes: of those within its reach, the one nearest the flag (ties to the lower id).
   * Measured from the flag, not the troop, so the whole formation focuses on the raider at its front instead of each
   * soldier picking its own nearest and spreading the damage (the stacked troops did this by standing on one point).
   */
  private nearestGroundEnemy(t: Troop, enemies: readonly Enemy[]): Enemy | null { return nearestToFlag(this.rules.engageRange, this.rally, t, enemies); }

  /** BFS from `from` to the rally flag; returns the cells after `from` (empty when already there or unreachable). */
  pathFrom(from: Cell, to: Cell = this.rally): Cell[] { return troopRoute(this.grid, (c) => this.walkable(c), from, to); }
}
